import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, Send, CheckCircle, Clock, XCircle, AlertCircle,
  Trophy, UserPlus, Shield, Filter, ChevronDown, Target
} from 'lucide-react';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Activity' },
  { value: 'lesson_completed', label: 'Lessons' },
  { value: 'submission', label: 'Submissions' },
  { value: 'mission_completed', label: 'Missions' },
  { value: 'reinforcement_completed', label: 'Reinforcements' },
];

const ActivityHistoryTab = ({ token }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get('/api/users/activity-history', config);
        setActivities(res.data);
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to load activity history');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [token]);

  const getIcon = (type, status) => {
    switch (type) {
      case 'lesson_completed':
        return <BookOpen size={18} className="text-blue-600" />;
      case 'mission_completed':
        return <Target size={18} className="text-purple-600" />;
      case 'reinforcement_completed':
        return <Shield size={18} className="text-indigo-600" />;
      case 'submission':
        if (status === 'approved') return <CheckCircle size={18} className="text-green-600" />;
        if (status === 'rejected') return <XCircle size={18} className="text-red-600" />;
        if (status === 'more_info_needed') return <AlertCircle size={18} className="text-orange-600" />;
        return <Clock size={18} className="text-yellow-600" />;
      case 'path_completed':
        return <Trophy size={18} className="text-amber-500" />;
      case 'account_created':
        return <UserPlus size={18} className="text-[#1086a4]" />;
      default:
        return <Clock size={18} className="text-gray-400" />;
    }
  };

  const getIconBg = (type, status) => {
    switch (type) {
      case 'lesson_completed': return 'bg-blue-50 border-blue-200';
      case 'mission_completed': return 'bg-purple-50 border-purple-200';
      case 'reinforcement_completed': return 'bg-indigo-50 border-indigo-200';
      case 'submission':
        if (status === 'approved') return 'bg-green-50 border-green-200';
        if (status === 'rejected') return 'bg-red-50 border-red-200';
        if (status === 'more_info_needed') return 'bg-orange-50 border-orange-200';
        return 'bg-yellow-50 border-yellow-200';
      case 'path_completed': return 'bg-amber-50 border-amber-200';
      case 'account_created': return 'bg-cyan-50 border-cyan-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getLabel = (type, status) => {
    switch (type) {
      case 'lesson_completed': return 'Completed Lesson';
      case 'mission_completed': return 'Completed Mission';
      case 'reinforcement_completed': return 'Completed Reinforcement';
      case 'submission':
        if (status === 'approved') return 'Submission Approved';
        if (status === 'rejected') return 'Submission Rejected';
        if (status === 'more_info_needed') return 'More Info Needed';
        return 'Submitted Mission';
      case 'path_completed': return 'Pathway Completed';
      case 'account_created': return 'Joined Platform';
      default: return 'Activity';
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    const styles = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700',
      more_info_needed: 'bg-orange-100 text-orange-700'
    };
    const labels = {
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      more_info_needed: 'More Info Needed'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Group activities by date
  const groupByDate = (items) => {
    const groups = {};
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
    const monthAgo = new Date(today); monthAgo.setDate(today.getDate() - 30);

    items.forEach(item => {
      const d = new Date(item.date);
      let group;
      if (d >= today) group = 'Today';
      else if (d >= yesterday) group = 'Yesterday';
      else if (d >= weekAgo) group = 'This Week';
      else if (d >= monthAgo) group = 'This Month';
      else group = 'Earlier';

      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });

    return groups;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filtered = filter === 'all'
    ? activities
    : activities.filter(a => a.type === filter);

  const grouped = groupByDate(filtered);
  const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-7 bg-gray-200 rounded w-1/3"></div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center py-8">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#054581]">Activity History</h2>
          <p className="text-sm text-gray-500 mt-1">{activities.length} total activities</p>
        </div>

        {/* Filter */}
        <div className="relative">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Filter size={16} />
            {FILTER_OPTIONS.find(f => f.value === filter)?.label}
            <ChevronDown size={14} className={`transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showFilterDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowFilterDropdown(false)} />
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                {FILTER_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setFilter(opt.value); setShowFilterDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      filter === opt.value ? 'text-[#1086a4] font-semibold bg-[#1086a4]/5' : 'text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="px-6 py-4">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Clock size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No activity yet</p>
            <p className="text-gray-400 text-sm mt-1">Your learning journey will appear here</p>
          </div>
        ) : (
          groupOrder.map(groupName => {
            const items = grouped[groupName];
            if (!items || items.length === 0) return null;
            return (
              <div key={groupName} className="mb-6 last:mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
                  {groupName}
                </h3>
                <div className="space-y-1">
                  {items.map((activity, idx) => (
                    <div
                      key={`${activity.type}-${activity.contentId || ''}-${idx}`}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center ${getIconBg(activity.type, activity.status)}`}>
                        {getIcon(activity.type, activity.status)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                              {getLabel(activity.type, activity.status)}
                            </span>
                            <p className="text-sm font-semibold text-[#1e2d40] mt-0.5">
                              {activity.title}
                            </p>
                            {/* Course link */}
                            {activity.courseTitle && (
                              <button
                                onClick={() => navigate(`/dashboard`)}
                                className="text-xs text-[#1086a4] hover:text-[#054581] hover:underline mt-0.5 inline-flex items-center gap-1"
                              >
                                <BookOpen size={12} />
                                {activity.courseTitle}
                              </button>
                            )}
                            {/* Submission status badge */}
                            {activity.type === 'submission' && activity.status && (
                              <div className="mt-1">
                                {getStatusBadge(activity.status)}
                              </div>
                            )}
                          </div>
                          {/* Date */}
                          <div className="flex-shrink-0 text-right">
                            <span className="text-xs text-gray-400">
                              {groupName === 'Today' || groupName === 'Yesterday'
                                ? formatTime(activity.date)
                                : formatDate(activity.date)
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.token
});

export default connect(mapStateToProps)(ActivityHistoryTab);
